import React from 'react';

// THE END OF THE POST YOU ARE ANSWERING STAYS IN VIEW.
//
// Nicole, 2026-10-23, in #eat speak rp repeat, with a screenshot: "whenever I
// type a long response it keeps cutting off the end of yours … the scroll on
// this is weird."
//
// Every chat view is a flex column: a scroll list (`flex: 1; overflow-y: auto`)
// above a composer that grows with the draft (textarea up to 50vh). When the
// composer grows by Δ the list's visible height shrinks by Δ but its scrollTop
// does not move, so the bottom Δ pixels of whatever you were reading slide
// behind the composer, and a writer answering a long RP post loses the last
// paragraph of it exactly while she is replying. Messaging apps keep the
// BOTTOM edge anchored instead: when the visible height changes, move scrollTop
// by the same amount (the top scrolls away, the end stays), and if you were
// following the live end, stay pinned to it. The same applies to a phone
// keyboard opening and to a window resize.
//
// One hook, five surfaces (channels, character DMs, player DMs, groups, stories).

export const NEAR_BOTTOM_PX = 150;

/** True when the list is within `px` of its end (the app's own "following the live end" test). */
export function isNearBottom({ scrollTop, scrollHeight, clientHeight }, px = NEAR_BOTTOM_PX) {
  return scrollHeight - scrollTop - clientHeight < px;
}

/**
 * Pure: where scrollTop should be after the list's visible height changed from
 * `prevClientHeight` to `clientHeight`.
 *  - never measured, or no change: leave it alone
 *  - was following the end: pin to the end
 *  - was reading up-thread: move by the lost (or gained) height so the content
 *    that sat at the bottom edge stays at the bottom edge
 */
export function anchoredScrollTop({ scrollTop, scrollHeight, prevClientHeight, clientHeight, wasNearBottom }) {
  if (!(prevClientHeight > 0) || !(clientHeight >= 0) || prevClientHeight === clientHeight) return scrollTop;
  if (wasNearBottom) return Math.max(0, scrollHeight - clientHeight);
  return Math.max(0, scrollTop + (prevClientHeight - clientHeight));
}

// The chat lists currently mounted, so a composer can protect their scroll position
// while it re-measures itself (see autoGrowTextarea).
const activeScrollers = new Set();

function detach(st) {
  if (st.ro) { st.ro.disconnect(); st.ro = null; }
  if (st.el && st.onScroll) st.el.removeEventListener('scroll', st.onScroll);
  if (st.el) activeScrollers.delete(st.el);
  st.onScroll = null;
  st.el = null;
}

/**
 * Size a composer textarea to its text WITHOUT moving the chat list above it.
 *
 * Every composer re-measures with `height = 'auto'` then `height = scrollHeight`.
 * The first step makes the composer a single row for an instant, which makes the
 * list above it taller for an instant, and a browser clamps a scroll position that
 * is suddenly past the new end of its content. When the composer grows back the
 * list does not follow: scrollTop has been left Δ pixels higher. That is "when I
 * type a lot it scrolls up in the messages just a little bit for some odd reason"
 * (Nicole, 2026-10-23, with before/after screenshots: she touches nothing but the
 * keyboard, and the end of the post she is answering disappears behind her box).
 * It gets worse the longer the draft, because the collapse is deeper, and once it
 * is deeper than NEAR_BOTTOM_PX the anchor below stops treating her as following
 * the end, so the list stays stuck up there.
 *
 * Measured in real browsers on 2026-10-24 (typing a long reply, worst gap from the
 * end of the thread): Chrome 156 puts the clamp back by itself (0 px with only the
 * anchor), Firefox 158 stayed 250 px stuck and Mobile Safari 334 px stuck, and with
 * this helper all three are 0 px. jsdom cannot show it, which is why v427 passed its
 * tests and still failed Nicole. The harness and numbers are in Documents/05 - Claude
 * & AI Work/Claude Projects/Projects/MFML/scroll-harness-2026-10-24.
 *
 * So: remember where every mounted list was, collapse, grow, put them back. All of
 * it happens inside one task, before paint, so nothing flickers; the bottom-anchor
 * observer then applies the net change in the composer's height.
 */
export function autoGrowTextarea(el) {
  if (!el) return;
  const saved = [];
  activeScrollers.forEach((s) => saved.push([s, s.scrollTop]));
  el.style.height = 'auto';
  el.style.height = el.scrollHeight + 'px';
  for (const [s, top] of saved) if (s.scrollTop !== top) s.scrollTop = top;
}

/**
 * Attach to the scroll container behind `ref`. The container can mount late or
 * be replaced (a channel or conversation is picked after first render), so the
 * effect runs after every commit and does nothing unless the element changed.
 */
export function useBottomAnchor(ref) {
  const st = React.useRef({ el: null, ro: null, onScroll: null });
  React.useEffect(() => {
    const el = ref.current;
    if (el === st.current.el) return;
    detach(st.current);
    if (!el) return;
    st.current.el = el;
    // Registered before the ResizeObserver check: the composer's re-measure must not
    // move this list even where there is no observer to re-anchor it afterwards.
    activeScrollers.add(el);
    if (typeof ResizeObserver === 'undefined') return;
    let prevH = el.clientHeight;
    let near = isNearBottom(el);
    const onScroll = () => { near = isNearBottom(el); };
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(() => {
      const h = el.clientHeight;
      if (h === prevH) return;
      const next = anchoredScrollTop({ scrollTop: el.scrollTop, scrollHeight: el.scrollHeight, prevClientHeight: prevH, clientHeight: h, wasNearBottom: near });
      prevH = h;
      if (next !== el.scrollTop) el.scrollTop = next;
    });
    ro.observe(el);
    st.current.ro = ro;
    st.current.onScroll = onScroll;
  });
  React.useEffect(() => () => detach(st.current), []);
}
